import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import { ChakraBaseProvider, ColorModeScript } from '@chakra-ui/react'

import theme from './theme/theme.ts'
import { QueryClient, QueryClientProvider } from 'react-query'

import { Helmet } from 'react-helmet'
import React from 'react'
import { Capacitor } from '@capacitor/core'
import { onCLS, onINP, onLCP, onTTFB } from 'web-vitals'

const HelmetSafe = Helmet as unknown as React.ComponentType<React.PropsWithChildren<Record<string, unknown>>>

// Recover from a failed lazy-chunk load instead of white-screening. A route's
// dynamic import() can fail transiently — e.g. a CDN 520 during the post-deploy
// cache-warm window, a flaky network, or a stale chunk after a new deploy. React
// has no built-in recovery, so the page goes blank. Reload once to fetch fresh
// chunks, guarded against reload loops (at most one reload per 15s) so a
// persistent failure stays visible rather than trapping the user in a loop.
const recoverFromChunkError = () => {
  const KEY = 'chunk-reload-ts'
  const last = Number(sessionStorage.getItem(KEY) || '0')
  if (Date.now() - last > 15_000) {
    sessionStorage.setItem(KEY, String(Date.now()))
    window.location.reload()
  }
}
const isChunkLoadError = (msg: string): boolean =>
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i.test(
    msg,
  )
// Vite fires this when its preload helper can't load a chunk.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()
  recoverFromChunkError()
})
// Belt-and-suspenders: the raw import() rejection when React.lazy loads a route.
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason as { message?: string } | undefined
  if (isChunkLoadError(String((reason && reason.message) || reason || ''))) {
    recoverFromChunkError()
  }
})

// A bare QueryClient defaults to staleTime:0 + refetchOnWindowFocus, so the same
// endpoints get refetched on every tab navigation and every window focus — measured
// /member/ ×18 and /venues/ ×17 in a single admin session. These defaults dedupe
// those repeats without hiding real updates (mutations still invalidate explicitly).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000, // 1 min: reuse cached data across navigations instead of refetching
      cacheTime: 10 * 60_000, // keep unmounted query data warm for 10 min
      refetchOnWindowFocus: false, // stop refetch storm on every tab/window focus
      refetchOnReconnect: false,
      retry: 1,
    },
  },
})

const DEFAULT_SENTRY_TRACES_SAMPLE_RATE = 0.02

const parseSampleRate = (value: string | undefined, fallback: number) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : fallback
}

// perf/audit — web-vitals RUM reporting (non-local envs only).
// Uses keepalive so the beacon survives page unload (required for LCP/CLS).
// VITE_API_ENDPOINT must be set in the env file (e.g. https://api.lvbl.club).
if (import.meta.env.VITE_IS_LOCAL !== 'true') {
  const rumEndpoint = `${import.meta.env.VITE_API_ENDPOINT ?? ''}/rum/vitals/`
  const reportVital = ({ name, value, id, delta, rating, navigationType }: {
    name: string
    value: number
    id: string
    delta?: number
    rating?: string
    navigationType?: string
  }) => {
    // fetch + keepalive survives page unload just like sendBeacon, but unlike
    // sendBeacon (which forces credentials:'include') we can send it anonymously.
    // The API's CORS is wildcard (Access-Control-Allow-Origin: *), and browsers
    // reject wildcard + credentials — so a credentialed beacon was failing CORS
    // preflight. This is anonymous telemetry, so credentials:'omit' is correct.
    fetch(rumEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, value, id, url: location.href, delta, rating, navigationType }),
      keepalive: true,
      credentials: 'omit',
      mode: 'cors',
    }).catch(() => {})
  }
  onLCP(reportVital)
  onINP(reportVital)
  onCLS(reportVital)
  onTTFB(reportVital)
}

// Lazy-load Sentry so it doesn't block the initial render
if (import.meta.env.VITE_IS_LOCAL !== 'true') {
  const SENTRY_DSN_WEB =
    'https://1c2e3341e6611eb39b92ff3f36e96277@o4510234459701248.ingest.us.sentry.io/4510900908720128'
  const SENTRY_DSN_CAPACITOR =
    'https://0ae4c8c44d51bd2c1f3d22c64b4ddaa2@o4510234459701248.ingest.us.sentry.io/4510900944240640'
  const sentryTracesSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
    DEFAULT_SENTRY_TRACES_SAMPLE_RATE
  )

  import('@sentry/capacitor').then((Sentry) => {
    const isNative = Capacitor.isNativePlatform()
    Sentry.init({
      dsn: isNative ? SENTRY_DSN_CAPACITOR : SENTRY_DSN_WEB,
      environment: import.meta.env.VITE_FIREBASE_ENV,
      sendDefaultPii: true,
      integrations: [
        Sentry.replayIntegration(),
        Sentry.browserTracingIntegration(),
      ],
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1,
      tracesSampleRate: sentryTracesSampleRate,
      tracePropagationTargets: [/^https:\/\/api\.lvbl\.club/],
      denyUrls: [/framer-motion/i, /style-value-types/i],
      ignoreErrors: [
        // Google Maps internal chunk loader failures — transient network errors, not actionable
        /Could not load "(util|places_impl|common|map|marker)"/,
      ],
      beforeSend(event) {
        const exceptionValues = event.exception?.values ?? []
        const messages = [
          event.message,
          ...exceptionValues.map((value) => value.value),
        ].filter(Boolean)
        const frames = exceptionValues.flatMap((value) => value.stacktrace?.frames ?? [])
        const hasGoogleMapsFrame = frames.some((frame) =>
          /^https:\/\/(maps\.googleapis\.com|maps\.gstatic\.com)\//.test(frame.filename ?? '')
        )
        const isTransientGoogleMapsLoadError =
          hasGoogleMapsFrame &&
          messages.some((message) =>
            /^(Load failed|Failed to fetch dynamically imported module|The Google Maps JavaScript API could not load)/.test(
              message ?? ''
            )
          )

        return isTransientGoogleMapsLoadError ? null : event
      },
      enableLogs: true,
    })
  })
}


ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <>
    <HelmetSafe>
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
    </HelmetSafe>
    <QueryClientProvider client={queryClient}>
      <ColorModeScript initialColorMode={theme.config.initialColorMode} />
      <ChakraBaseProvider theme={theme}>
        <App />
      </ChakraBaseProvider>
    </QueryClientProvider>
  </>,
)
